import type React from "react";
import { useMemo, useState } from "react";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { faNote } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse, IconButton } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

import { paginationModel, usePagination } from "api/hooks/usePagination";
import { useGetUnsignedNotes } from "api/services/admin/hooks";
import type { UnsignedNote } from "api/services/admin/schemas/unsigned_notes";
import { CPTCode } from "routes/dashboard/default/components/MedicalNotesTable/CPTCode";
import { WidgetBox } from "shared/containers/WidgetBox";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import type { MedicalNoteType } from "types";
import { useLocalizedDate } from "utils/date";
import { translateMedicalNoteType } from "utils/translations";

interface UnsignedNotesProps {
  hcoID: number;
}

export const UnsignedNotes: React.FC<UnsignedNotesProps> = ({ hcoID }) => {
  const { t } = useTranslation();
  const { format, parseISO } = useLocalizedDate();
  const { pagination, setPagination } = usePagination();
  const [expanded, setExpanded] = useState(true);

  const { data: unsignedNotesData, isLoading } = useGetUnsignedNotes({
    health_care_organization_id: hcoID,
    params: {
      limit: pagination.limit,
      page: pagination.page + 1,
    },
  });
  const unsignedNotes = unsignedNotesData?.items || [];

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "patient_id", headerName: t("admin.unsigned_notes.patient_id"), flex: 1 },
      { field: "patient_name", headerName: t("admin.unsigned_notes.patient_name"), flex: 1, minWidth: 160 },
      {
        field: "cpt_codes",
        headerName: t("admin.unsigned_notes.cpt_codes"),
        renderCell: (params: GridRenderCellParams<UnsignedNote, string[] | undefined>) => {
          return params.value?.[0] ? <CPTCode cptCode={params.value[0]} /> : null;
        },
        flex: 1,
        minWidth: 120,
      },
      {
        field: "label",
        headerName: t("admin.unsigned_notes.label"),
        valueFormatter: (value: string) => translateMedicalNoteType(value.toLocaleUpperCase() as MedicalNoteType),
        flex: 1,
        minWidth: 120,
      },
      { field: "therapist_name", headerName: t("admin.unsigned_notes.therapist_name"), flex: 1, minWidth: 160 },
      {
        field: "start_time",
        headerName: t("admin.unsigned_notes.start_time"),
        valueFormatter: (value: string) => (value ? format(parseISO(value), "PP") : ""),
        flex: 1,
        minWidth: 140,
      },
      { field: "review_state", headerName: t("admin.unsigned_notes.review_state"), flex: 1, minWidth: 130 },
    ],
    [t]
  );

  return (
    <>
      <WidgetBox
        title={t("admin.unsigned_notes.title")}
        description={t("admin.unsigned_notes.description", { count: unsignedNotesData?.metadata?.total_count ?? 0 })}
        icon={<FontAwesomeIcon icon={faNote} />}
        color="blue"
        toolbar={
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} style={{ fontSize: 14 }} />
          </IconButton>
        }
        skipPadding
      >
        <Collapse in={expanded}>
          <MUIDataGrid
            columns={columns}
            rows={unsignedNotes}
            getRowId={row => row.care_event_id}
            loading={isLoading}
            initialState={{ pagination: { paginationModel } }}
            paginationMode="server"
            onPaginationModelChange={model => setPagination({ limit: model.pageSize, page: model.page })}
            rowCount={unsignedNotesData?.metadata.total_count ?? 0}
            rowSelection={false}
            slots={{
              noRowsOverlay: () => <EmptyResultOverlay />,
              noResultsOverlay: () => <EmptyResultOverlay />,
            }}
            localeText={{
              noRowsLabel: "",
              noResultsOverlayLabel: "",
            }}
            disableColumnMenu
            hideHeader
            transparentBackground
            density="comfortable"
            sx={{
              "& .MuiDataGrid-row": {
                cursor: "default",
              },
            }}
          />
        </Collapse>
      </WidgetBox>
    </>
  );
};
